import type { FC } from 'react';
import { useHasPermission } from '@src/shared/hooks/use-has-permission';
import { entities } from '@src/shared/constants/entities';
import {
  EditableDropdownCell,
  EditableDropdownCellEditMode,
  EditableDropdownCellItem,
  EditableDropdownCellViewMode,
} from '@src/shared/components/editable-dropdown-cell';
import { formatTextForDisplay } from '@src/shared/utils/helpers/format-text-for-display';
import type { TLeadNote } from '@src/shared/types/lead-note';
import leadNoteApi from '@src/features/leads/services/lead-note-api';
import {
  getChannelIcon,
  NOTE_CHANNELS_OPTIONS,
} from '../../../list/dialogs/lead-notes-add-dialog/helpers';
import { useGetMe } from '@src/shared/services/get-me';

export const FollowUpChannelCell: FC<{
  channel: TLeadNote['channel'];
  note: TLeadNote;
}> = ({ channel, note }) => {
  const { mutateAsync: editNote, variables, error } = leadNoteApi.useUpdate();
  const { data: currentUser } = useGetMe();
  const isCurrentUserNoteCreator =
    currentUser?.id === note.created_by?.id ||
    currentUser?.id === note.created_by_partner?.id;

  const optimisticChannel = error ? channel : (variables?.channel ?? channel);

  const CurrentChannelIcon = getChannelIcon(optimisticChannel);

  const handleUpdateChannel = (channel: TLeadNote['channel']) => {
    if (!channel) return;

    if (note.id)
      editNote({
        id: note.id,
        channel,
      });
  };

  const hasPermission = useHasPermission();
  const canEditLead = hasPermission(entities.lead, 'change');

  if (!canEditLead || !isCurrentUserNoteCreator) {
    return (
      <span className='flex justify-start gap-1.5 p-0 border-none text-sm font-normal capitalize'>
        {CurrentChannelIcon && <CurrentChannelIcon size={18} />}
        {formatTextForDisplay(optimisticChannel ?? '-')}
      </span>
    );
  }

  return (
    <EditableDropdownCell>
      <EditableDropdownCellViewMode>
        <span className='flex items-center gap-2'>
          {CurrentChannelIcon && <CurrentChannelIcon size={18} />}
          {formatTextForDisplay(optimisticChannel ?? '-')}
        </span>
      </EditableDropdownCellViewMode>
      <EditableDropdownCellEditMode>
        {NOTE_CHANNELS_OPTIONS.map((opt) => (
          <EditableDropdownCellItem
            key={opt.value}
            isSelected={opt.value === optimisticChannel}
            onClick={() => handleUpdateChannel(opt.value)}
          >
            <span className='flex items-center gap-2'>
              <opt.icon size={20} />
              {formatTextForDisplay(opt.label)}
            </span>
          </EditableDropdownCellItem>
        ))}
      </EditableDropdownCellEditMode>
    </EditableDropdownCell>
  );
};
